import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ContentCopy,
    Download,
    Extension,
    History,
    LockOutlined,
    Verified,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../api';
import DesktopReleaseDownloadButtons from '../../components/DesktopReleaseDownloadButtons';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import useAlertModal from '../../components/AlertModal';

function IntegrationsPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [planName, setPlanName] = useState('');
    const [integrationKey, setIntegrationKey] = useState('');
    const [latestRelease, setLatestRelease] = useState(null);
    const { showAlert } = useAlertModal();

    useEffect(() => {
        Promise.all([
            api.get('me/integrations'),
            api.get('/desktop-releases/latest').catch(() => ({ data: { data: null } })),
        ])
            .then(([integrationsResponse, releaseResponse]) => {
                const payload = integrationsResponse?.data?.data;
                if (!payload) {
                    setDidNotLoad(true);
                    return;
                }
                setHasProAccess(Boolean(payload.has_pro_access));
                setPlanName(payload.plan_name || '');
                setIntegrationKey(payload.integration_key || '');
                setLatestRelease(releaseResponse?.data?.data || null);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleCopyKey = async () => {
        if (!integrationKey) {
            return;
        }

        try {
            await navigator.clipboard.writeText(integrationKey);
            showAlert({
                title: 'Copiado!',
                message: 'Sua chave de integração foi copiada para a área de transferência.',
                iconType: 'success',
            });
        } catch {
            showAlert({
                title: 'Não foi possível copiar',
                message: 'Selecione a chave manualmente e use Ctrl+C (ou Cmd+C).',
                iconType: 'warning',
            });
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    const requirements = [];
    if (latestRelease?.download_windows) {
        requirements.push('Windows 10 ou superior');
    }
    if (latestRelease?.download_mac) {
        requirements.push('macOS 12 (Monterey) ou superior');
    }
    if (latestRelease?.download_linux) {
        requirements.push('Linux (x86_64)');
    }

    return (
        <Box padding={2} maxWidth={920} mx="auto">
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <Extension color="primary" sx={{ fontSize: 36 }} />
                <Typography variant="h4" component="h1">
                    Integrações
                </Typography>
            </Stack>

            <Typography variant="body1" color="text.secondary" mb={3}>
                Conecte suas ferramentas e automatize fluxos com o Acompanha 3D.
            </Typography>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Nesta área você encontra as integrações disponíveis para a sua conta.
                Use a chave de integração para conectar impressoras, serviços e outros recursos compatíveis.
                Mantenha sua chave em segurança — ela identifica sua conta nos sistemas externos.
            </Alert>

            {hasProAccess ? (
                <Stack spacing={3}>
                    <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                        <Stack spacing={3}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                    icon={<Verified />}
                                    label="Integração ativa"
                                    color="success"
                                    variant="outlined"
                                />
                            </Stack>

                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Chave para conectar ao Acompanha 3D Farm Link
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={1.5}>
                                    Copie a chave abaixo e insira-a na aplicação{' '}
                                    <strong>Acompanha 3D Farm Link Desktop</strong> para sincronizar as
                                    impressoras da sua farm e habilitar o report automático de impressões,
                                    além de outros recursos incluídos no seu plano.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Por segurança, mantenha a chave em sigilo. Ela pode ser regenerada ou
                                    desativada pelo suporte se necessário.
                                </Typography>

                                <TextField
                                    fullWidth
                                    value={integrationKey}
                                    InputProps={{
                                        readOnly: true,
                                        sx: { fontFamily: 'monospace', fontSize: '0.95rem' },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleCopyKey}
                                                    edge="end"
                                                    aria-label="Copiar chave de integração"
                                                    color="primary"
                                                >
                                                    <ContentCopy />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<ContentCopy />}
                                    onClick={handleCopyKey}
                                    sx={{ mt: 2 }}
                                >
                                    Copiar chave
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>

                    <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                        <Stack spacing={2.5}>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Download color="primary" />
                                <Typography variant="h6" component="h2">
                                    Download do Acompanha 3D Farm Link Desktop
                                </Typography>
                                {latestRelease?.version && (
                                    <Chip size="small" color="primary" variant="outlined" label={`v${latestRelease.version}`} />
                                )}
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                Baixe, descompate e execute o aplicativo no computador que gerencia sua farm de impressoras.
                                Abra o Farm Link, insira sua chave de integração e
                                sincronize suas impressoras — com tudo configurado, os dados da farm
                                ficam prontos para serem atualizados em tempo real no Acompanha 3D.
                            </Typography>

                            {latestRelease?.title && (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {latestRelease.title}
                                    </Typography>
                                    {latestRelease.changelog && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                                        >
                                            {latestRelease.changelog}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {latestRelease ? (
                                <DesktopReleaseDownloadButtons release={latestRelease} />
                            ) : (
                                <Alert severity="warning">
                                    Nenhuma versão publicada no momento. Volte em breve.
                                </Alert>
                            )}

                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                                {requirements.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        Requisitos: {requirements.join(' · ')}
                                    </Typography>
                                )}
                                <Link
                                    component={RouterLink}
                                    to="/admin/farm-link-changelog"
                                    underline="hover"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <History fontSize="small" />
                                    Histórico de versões
                                </Link>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            ) : (
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        textAlign: 'center',
                        background: (theme) => (
                            theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, rgba(25,118,210,0.12) 0%, rgba(0,0,0,0) 100%)'
                                : 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 100%)'
                        ),
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'action.hover',
                        }}
                    >
                        <LockOutlined color="primary" sx={{ fontSize: 36 }} />
                    </Box>

                    <Typography variant="h5" gutterBottom>
                        Integrações indisponíveis
                    </Typography>

                    <Typography variant="body1" color="text.secondary" mb={1} maxWidth={560} mx="auto">
                        Este recurso não está disponível na sua conta.
                    </Typography>

                    {planName && planName !== 'Free' && planName !== 'free' && (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Plano atual: {planName}
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" maxWidth={560} mx="auto">
                        Se você precisa de integração com impressoras, fale com o suporte.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}

export default IntegrationsPage;
